import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, X } from "lucide-react";
import { z } from "zod";
import {
  CA_POSTAL_RE,
  CONTACT_LANGUAGES,
  ContactChannelTypeSchema,
  ContactLanguageSchema,
  EmailSchema,
  PhoneE164Schema,
  PreferredCommunicationChannelSchema,
  StateOrProvinceCodeSchema,
  US_ZIP_RE,
  ZipOrPostalCodeSchema,
  stateOptionsForCountry,
  type Contact,
  type ContactChannel,
  type ContactLanguage,
  type UpdateContactDto,
} from "@lumika/shared";
import {
  Badge,
  Button,
  ConfirmationDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  PhoneInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  toast,
  useZodForm,
} from "@lumika/ui";
import { authApi } from "../api/auth";
import { bdcApi } from "../api/bdc";
import { contactDisplayName } from "../lib/contact";

/**
 * Contact edit/create dialog — the single edit surface for a contact's
 * profile, address, channels, and preferences. Shared by the Contacts
 * directory and the lead-screen Customer 360 panel so editing
 * a contact never duplicates form logic or drifts between surfaces.
 */

type ContactChannelType = z.infer<typeof ContactChannelTypeSchema>;
type PreferredChannel = z.infer<typeof PreferredCommunicationChannelSchema>;

const CHANNEL_TYPE_LABELS: Record<ContactChannelType, string> = {
  phone: "Phone",
  email: "Email",
  web: "Web",
  telegram: "Telegram",
  messenger: "Messenger",
  instagram: "Instagram",
  whatsapp: "WhatsApp",
};

/**
 * Per-type labelling for the channel's identifier. The underlying field
 * is always `externalId`, but "External ID" means nothing to an operator —
 * so we show what they're actually typing (an email, a phone, a handle).
 */
const CHANNEL_ID_FIELD: Record<
  ContactChannelType,
  { label: string; placeholder: string; description: string }
> = {
  phone: {
    label: "Phone number",
    placeholder: "+15551234567",
    description: "Mobile or landline in E.164 format, e.g. +15551234567.",
  },
  email: {
    label: "Email address",
    placeholder: "name@example.com",
    description: "The email address messages to this contact go to.",
  },
  whatsapp: {
    label: "WhatsApp number",
    placeholder: "+15551234567",
    description: "WhatsApp number in E.164 format, e.g. +15551234567.",
  },
  telegram: {
    label: "Telegram username",
    placeholder: "@username",
    description: "Telegram @username or numeric chat id.",
  },
  messenger: {
    label: "Messenger handle",
    placeholder: "@page-handle",
    description: "Facebook Messenger handle or page-scoped id.",
  },
  instagram: {
    label: "Instagram handle",
    placeholder: "@handle",
    description: "Instagram @handle or account id.",
  },
  web: {
    label: "Web visitor ID",
    placeholder: "visitor id",
    description: "The website widget's anonymous visitor id.",
  },
};

const ContactFormSchema = z.object({
  firstName: z.string().trim().max(80).optional(),
  lastName: z.string().trim().max(80).optional(),
  businessAccount: z.boolean(),
  businessName: z.string().trim().max(120).optional(),
  primaryEmail: z.string().email("Invalid email.").or(z.literal("")).optional(),
  primaryPhone: z.union([z.literal(""), PhoneE164Schema]).optional(),
  // `YYYY-MM-DD` from the native date input, or "" when cleared.
  birthDate: z
    .union([z.literal(""), z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD.")])
    .optional(),
  state: z.union([z.literal(""), StateOrProvinceCodeSchema]).optional(),
  city: z.string().trim().max(80).optional(),
  zip: z.union([z.literal(""), ZipOrPostalCodeSchema]).optional(),
  addressLine1: z.string().trim().max(160).optional(),
  // "none" is the form sentinel for null — preserves the distinction
  // between "no preference set" (null → "none") and explicit "en".
  preferredLanguage: ContactLanguageSchema.or(z.literal("none")),
  preferredChannel: PreferredCommunicationChannelSchema.or(z.literal("none")),
  leadSource: z.string().trim().max(120).optional(),
  optoutEmail: z.boolean(),
  optoutSms: z.boolean(),
  optoutVoice: z.boolean(),
  notes: z.string().trim().max(4000).optional(),
}).superRefine((data, ctx) => {
  if (data.businessAccount === true && !(data.businessName ?? "").trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["businessName"],
      message: "Required for business accounts.",
    });
  }
});

type ContactFormValues = z.infer<typeof ContactFormSchema>;

// The base zip accepts either country's shape (the server stores the
// union); the dealership's country narrows the format here. The state
// dropdown only offers the country's own codes, so it needs no rule.
function contactFormSchemaForCountry(country: string | undefined) {
  const isCa = country === "CA";
  return ContactFormSchema.superRefine((values, ctx) => {
    if (values.zip && !(isCa ? CA_POSTAL_RE : US_ZIP_RE).test(values.zip)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["zip"],
        message: isCa
          ? "Use a Canadian postal code, e.g. A1A 1A1."
          : "Use ZIP (12345) or ZIP+4 (12345-6789).",
      });
    }
  });
}

const EMPTY: ContactFormValues = {
  firstName: "",
  lastName: "",
  businessAccount: false,
  businessName: "",
  primaryEmail: "",
  primaryPhone: "",
  birthDate: "",
  state: "",
  city: "",
  zip: "",
  addressLine1: "",
  preferredLanguage: "none" as ContactLanguage | "none",
  preferredChannel: "none",
  leadSource: "",
  optoutEmail: false,
  optoutSms: false,
  optoutVoice: false,
  notes: "",
};

function toEditValues(contact: Contact): ContactFormValues {
  return {
    firstName: contact.firstName ?? "",
    lastName: contact.lastName ?? "",
    businessAccount: contact.businessAccount,
    businessName: contact.businessName ?? "",
    primaryEmail: contact.primaryEmail ?? "",
    primaryPhone: contact.primaryPhone ?? "",
    birthDate: contact.birthDate ?? "",
    state: contact.state ?? "",
    city: contact.city ?? "",
    zip: contact.zip ?? "",
    addressLine1: contact.addressLine1 ?? "",
    preferredLanguage: (contact.preferredLanguage as ContactLanguage | null) ?? "none",
    preferredChannel: (contact.preferredChannel as PreferredChannel | null) ?? "none",
    leadSource: contact.leadSource ?? "",
    optoutEmail: contact.optoutEmail,
    optoutSms: contact.optoutSms,
    optoutVoice: contact.optoutVoice,
    notes: contact.notes ?? "",
  };
}

function toPayload(values: ContactFormValues): UpdateContactDto {
  const text = (value: string | undefined): string | null =>
    value && value.trim() ? value.trim() : null;
  return {
    firstName: text(values.firstName),
    lastName: text(values.lastName),
    businessAccount: values.businessAccount,
    // The business-name field is hidden for individuals, so its stale
    // value must be cleared — not resent — when the toggle goes off.
    businessName: values.businessAccount ? text(values.businessName) : null,
    primaryEmail: text(values.primaryEmail),
    primaryPhone: text(values.primaryPhone),
    birthDate: text(values.birthDate),
    state: text(values.state),
    city: text(values.city),
    zip: text(values.zip),
    addressLine1: text(values.addressLine1),
    preferredLanguage: values.preferredLanguage === "none" ? null : values.preferredLanguage,
    preferredChannel: values.preferredChannel === "none" ? null : values.preferredChannel,
    leadSource: text(values.leadSource),
    optoutEmail: values.optoutEmail,
    optoutSms: values.optoutSms,
    optoutVoice: values.optoutVoice,
    notes: text(values.notes),
  };
}

export function ContactFormDialog({
  mode,
  contact,
  onClose,
  onSaved,
}: {
  mode: "create" | "edit";
  contact: Contact | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const meQuery = useQuery({ queryKey: ["me"], queryFn: authApi.me });
  const country = meQuery.data?.dealership.country ?? "US";
  const isCa = country === "CA";
  const formSchema = useMemo(() => contactFormSchemaForCountry(country), [country]);
  const initialValues = useMemo(
    () => (contact ? toEditValues(contact) : EMPTY),
    [contact],
  );
  const form = useZodForm(formSchema, initialValues);

  const createMutation = useMutation({
    mutationFn: (input: ContactFormValues) =>
      bdcApi.createContact(toPayload(input) as Parameters<typeof bdcApi.createContact>[0]),
    onSuccess: async () => {
      onSaved();
      toast.success("Contact created");
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: (input: ContactFormValues) =>
      bdcApi.updateContact(contact!.id, toPayload(input)),
    onSuccess: async () => {
      onSaved();
      toast.success("Contact updated");
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function submit() {
    const result = form.validate();
    if (!result.ok) return;
    if (mode === "create") createMutation.mutate(result.data);
    else updateMutation.mutate(result.data);
  }

  const { values, errors, setField } = form;
  const isPending = createMutation.isPending || updateMutation.isPending;
  const isBusiness = values.businessAccount;
  // Once the operator has changed anything (or has a channel draft
  // open), outside-click/Escape must not silently discard it — X and
  // Close still work.
  const [addingChannel, setAddingChannel] = useState(false);
  const isDirty =
    addingChannel || JSON.stringify(values) !== JSON.stringify(initialValues);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl" preventDismiss={isDirty}>
        <DialogHeader>
          <DialogTitle>
            {mode === "create"
              ? "New contact"
              : contact
                ? `Edit ${contactDisplayName(contact)}`
                : "Edit contact"}
          </DialogTitle>
          <DialogDescription>
            Profile, address, channels, and preferences.
          </DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[60vh] gap-5 overflow-y-auto -mx-1 px-1">
          <FieldGroup title="Identity">
            <FormField
              className="sm:col-span-2"
              label="Business account"
              description="Toggle on for fleet, dealer-to-dealer, or commercial customers."
            >
              <div className="flex items-center gap-3">
                <Switch
                  checked={values.businessAccount}
                  onCheckedChange={(checked) => setField("businessAccount", checked)}
                />
                <span className="text-sm text-muted-foreground">
                  {isBusiness ? "Business" : "Individual"}
                </span>
              </div>
            </FormField>
            {isBusiness && (
              <FormField
                className="sm:col-span-2"
                label="Business name"
                required
                error={errors.businessName}
              >
                <Input
                  value={values.businessName ?? ""}
                  onChange={(e) => setField("businessName", e.target.value)}
                  placeholder="Acme Logistics"
                />
              </FormField>
            )}
            <FormField label="First name" optional error={errors.firstName}>
              <Input
                value={values.firstName ?? ""}
                onChange={(e) => setField("firstName", e.target.value)}
                autoFocus={mode === "create"}
              />
            </FormField>
            <FormField label="Last name" optional error={errors.lastName}>
              <Input
                value={values.lastName ?? ""}
                onChange={(e) => setField("lastName", e.target.value)}
              />
            </FormField>
            <FormField label="Primary email" optional error={errors.primaryEmail}>
              <Input
                type="email"
                value={values.primaryEmail ?? ""}
                onChange={(e) => setField("primaryEmail", e.target.value)}
              />
            </FormField>
            <FormField label="Primary phone" optional error={errors.primaryPhone}>
              <PhoneInput
                value={values.primaryPhone ?? ""}
                onChange={(v) => setField("primaryPhone", v)}
              />
            </FormField>
            <FormField label="Birthday" optional error={errors.birthDate}>
              <Input
                type="date"
                value={values.birthDate ?? ""}
                onChange={(e) => setField("birthDate", e.target.value)}
              />
            </FormField>
          </FieldGroup>

          <FieldGroup title="Address">
            <FormField className="sm:col-span-2" label="Street" optional error={errors.addressLine1}>
              <Input
                value={values.addressLine1 ?? ""}
                onChange={(e) => setField("addressLine1", e.target.value)}
              />
            </FormField>
            <FormField label="City" optional error={errors.city}>
              <Input
                value={values.city ?? ""}
                onChange={(e) => setField("city", e.target.value)}
              />
            </FormField>
            <FormField label={isCa ? "Province" : "State"} optional error={errors.state}>
              <Select
                value={values.state ?? ""}
                onValueChange={(v) => setField("state", v === "none" ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="— None —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {/* Imported contacts can carry the other country's code —
                      keep it selectable so the trigger isn't blank. */}
                  {values.state &&
                    !stateOptionsForCountry(country).some((s) => s.code === values.state) && (
                      <SelectItem value={values.state}>{values.state}</SelectItem>
                    )}
                  {stateOptionsForCountry(country).map((state) => (
                    <SelectItem key={state.code} value={state.code}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label={isCa ? "Postal code" : "ZIP"} optional error={errors.zip}>
              <Input
                value={values.zip ?? ""}
                onChange={(e) => setField("zip", e.target.value)}
                placeholder={isCa ? "A1A 1A1" : "12345 or 12345-6789"}
              />
            </FormField>
          </FieldGroup>

          <FieldGroup title="Preferences">
            <FormField className="sm:col-span-2" label="Language" error={errors.preferredLanguage}>
              <Select
                value={values.preferredLanguage}
                onValueChange={(v) =>
                  setField("preferredLanguage", v as ContactLanguage | "none")
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not set</SelectItem>
                  {CONTACT_LANGUAGES.map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField className="sm:col-span-2" label="Preferred channel" error={errors.preferredChannel}>
              <Select
                value={values.preferredChannel}
                onValueChange={(v) =>
                  setField("preferredChannel", v as ContactFormValues["preferredChannel"])
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No preference</SelectItem>
                  {PreferredCommunicationChannelSchema.options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </FieldGroup>

          {mode === "edit" && contact && (
            <ContactChannelsEditor
              contact={contact}
              adding={addingChannel}
              setAdding={setAddingChannel}
            />
          )}

          {/* Positive consent framing — toggles read "allowed" (on =
              contactable) to match the detail page's EMAIL OK / SMS OK
              badges. The wire fields stay the optout_* booleans, so the
              switch state is inverted at this boundary. */}
          <FieldGroup title="Marketing & consent">
            <FormField label="Email allowed">
              <Switch
                checked={!values.optoutEmail}
                onCheckedChange={(checked) => setField("optoutEmail", !checked)}
              />
            </FormField>
            <FormField label="SMS allowed">
              <Switch
                checked={!values.optoutSms}
                onCheckedChange={(checked) => setField("optoutSms", !checked)}
              />
            </FormField>
            <FormField label="Voice allowed">
              <Switch
                checked={!values.optoutVoice}
                onCheckedChange={(checked) => setField("optoutVoice", !checked)}
              />
            </FormField>
            <FormField label="Lead source" optional error={errors.leadSource}>
              <Input
                value={values.leadSource ?? ""}
                onChange={(e) => setField("leadSource", e.target.value)}
                placeholder="cars.com / walk-in"
              />
            </FormField>
          </FieldGroup>

          <FieldGroup title="Notes">
            <FormField className="sm:col-span-2" label="Notes" optional error={errors.notes}>
              <Textarea
                value={values.notes ?? ""}
                onChange={(e) => setField("notes", e.target.value)}
                rows={3}
                placeholder="Visible to all operators."
              />
            </FormField>
          </FieldGroup>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {mode === "edit" ? "Close" : "Cancel"}
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {isPending
              ? "Saving…"
              : mode === "create"
                ? "Create contact"
                : "Save contact"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ChannelDraftSchema = z
  .object({
    type: ContactChannelTypeSchema,
    externalId: z.string().trim().min(1, "Required."),
    value: z.string().trim().max(160).optional(),
    label: z.string().trim().max(80).optional(),
    isPrimary: z.boolean(),
  })
  .superRefine((draft, ctx) => {
    if (draft.type === "email" && !EmailSchema.safeParse(draft.externalId).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["externalId"],
        message: "Enter a valid email address.",
      });
    }
    if (draft.type === "phone" && !PhoneE164Schema.safeParse(draft.externalId).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["externalId"],
        message: "Use E.164 format, e.g. +15551234567.",
      });
    }
  });

type ChannelDraft = z.infer<typeof ChannelDraftSchema>;

// `adding` is lifted to the dialog so an open channel draft also
// blocks accidental outside-click/Escape dismissal.
function ContactChannelsEditor({
  contact,
  adding,
  setAdding,
}: {
  contact: Contact;
  adding: boolean;
  setAdding: (adding: boolean) => void;
}) {
  const qc = useQueryClient();
  const [removeTarget, setRemoveTarget] = useState<ContactChannel | null>(null);
  const draftForm = useZodForm(ChannelDraftSchema, {
    type: "email",
    externalId: "",
    value: "",
    label: "",
    isPrimary: false,
  });

  const channelsQuery = useQuery({
    queryKey: ["bdc", "contact-channels", contact.id],
    queryFn: () => bdcApi.contactChannels(contact.id),
  });

  const channels = useMemo(() => channelsQuery.data?.items ?? [], [channelsQuery.data]);

  const createMutation = useMutation({
    mutationFn: (input: ChannelDraft) =>
      bdcApi.createContactChannel({
        contactId: contact.id,
        type: input.type,
        externalId: input.externalId.trim(),
        value: input.value?.trim() ? input.value.trim() : null,
        label: input.label?.trim() ? input.label.trim() : null,
        isPrimary: input.isPrimary,
        isMarketingOptedIn: false,
      }),
    onSuccess: async () => {
      setAdding(false);
      draftForm.reset();
      await invalidateChannels(qc, contact.id);
      toast.success("Channel added");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const togglePrimary = useMutation({
    mutationFn: ({ id, isPrimary }: { id: string; isPrimary: boolean }) =>
      bdcApi.updateContactChannel(id, { isPrimary }),
    onSuccess: () => invalidateChannels(qc, contact.id),
    onError: (error: Error) => toast.error(error.message),
  });

  const toggleOptIn = useMutation({
    mutationFn: ({ id, isMarketingOptedIn }: { id: string; isMarketingOptedIn: boolean }) =>
      bdcApi.updateContactChannel(id, { isMarketingOptedIn }),
    onSuccess: () => invalidateChannels(qc, contact.id),
    onError: (error: Error) => toast.error(error.message),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => bdcApi.removeContactChannel(id),
    onSuccess: async () => {
      await invalidateChannels(qc, contact.id);
      setRemoveTarget(null);
      toast.success("Channel removed");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function addChannel() {
    const result = draftForm.validate();
    if (!result.ok) return;
    createMutation.mutate(result.data);
  }

  const idField = CHANNEL_ID_FIELD[draftForm.values.type];

  return (
    <fieldset className="grid gap-3">
      <legend className="flex w-full items-center justify-between text-sm font-semibold text-muted-foreground">
        Channels
        {!adding && (
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="h-3 w-3" />
            Add channel
          </Button>
        )}
      </legend>

      {channels.length === 0 && !adding && (
        <div className="rounded-md border border-dashed bg-muted/30 p-4 text-center text-sm text-muted-foreground">
          No channels yet. Phone, email, and social handles attach the contact to specific lead surfaces.
        </div>
      )}

      {channels.map((channel: ContactChannel) => (
        <div
          key={channel.id}
          className="grid gap-3 rounded-md border bg-card p-3 sm:grid-cols-[1fr_1fr_auto]"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{CHANNEL_TYPE_LABELS[channel.type]}</Badge>
              <span className="truncate font-mono text-sm">{channel.externalId}</span>
            </div>
            {(channel.value || channel.label) && (
              <div className="mt-1 text-xs text-muted-foreground">
                {[channel.label, channel.value].filter(Boolean).join(" · ")}
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <Switch
                checked={channel.isPrimary}
                onCheckedChange={(checked) =>
                  togglePrimary.mutate({ id: channel.id, isPrimary: checked })
                }
              />
              <span>Primary</span>
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <Switch
                checked={channel.isMarketingOptedIn}
                onCheckedChange={(checked) =>
                  toggleOptIn.mutate({ id: channel.id, isMarketingOptedIn: checked })
                }
              />
              <span>Marketing opt-in</span>
            </label>
          </div>
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Remove channel ${channel.externalId}`}
            className="text-destructive hover:text-destructive"
            onClick={() => setRemoveTarget(channel)}
            disabled={removeMutation.isPending}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}

      {adding && (
        <div className="grid gap-3 rounded-md border bg-card p-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Type" required>
              <Select
                value={draftForm.values.type}
                onValueChange={(v) =>
                  draftForm.setField("type", v as ContactChannelType)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ContactChannelTypeSchema.options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {CHANNEL_TYPE_LABELS[option]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField
              label={idField.label}
              required
              error={draftForm.errors.externalId}
              description={idField.description}
            >
              <Input
                value={draftForm.values.externalId}
                onChange={(e) => draftForm.setField("externalId", e.target.value)}
                placeholder={idField.placeholder}
                autoFocus
              />
            </FormField>
            <FormField label="Display value" optional error={draftForm.errors.value}>
              <Input
                value={draftForm.values.value ?? ""}
                onChange={(e) => draftForm.setField("value", e.target.value)}
              />
            </FormField>
            <FormField label="Label" optional error={draftForm.errors.label}>
              <Input
                value={draftForm.values.label ?? ""}
                onChange={(e) => draftForm.setField("label", e.target.value)}
                placeholder="work / personal"
              />
            </FormField>
          </div>
          <label className="flex cursor-pointer items-center gap-3 text-sm">
            <Switch
              checked={draftForm.values.isPrimary}
              onCheckedChange={(checked) => draftForm.setField("isPrimary", checked)}
            />
            <span>Mark as primary for this type</span>
          </label>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setAdding(false);
                draftForm.reset();
              }}
            >
              <X className="h-4 w-4" />
              Cancel
            </Button>
            <Button size="sm" onClick={addChannel} disabled={createMutation.isPending}>
              {createMutation.isPending ? "Adding…" : "Add channel"}
            </Button>
          </div>
        </div>
      )}
      <ConfirmationDialog
        open={removeTarget !== null}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
        title="Remove this contact channel?"
        description={
          removeTarget
            ? `${CHANNEL_TYPE_LABELS[removeTarget.type]} ${removeTarget.externalId} will be detached from this contact. Existing conversation history remains available.`
            : "This channel will be detached from the contact."
        }
        confirmLabel="Remove channel"
        destructive
        pending={removeMutation.isPending}
        onConfirm={() => removeTarget && removeMutation.mutate(removeTarget.id)}
      />
    </fieldset>
  );
}

function FieldGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="grid gap-3">
      <legend className="text-sm font-semibold text-muted-foreground">
        {title}
      </legend>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

async function invalidateChannels(qc: ReturnType<typeof useQueryClient>, contactId: string) {
  await Promise.all([
    qc.invalidateQueries({ queryKey: ["bdc", "contact-channels", contactId] }),
    qc.invalidateQueries({ queryKey: ["bdc", "contacts"] }),
  ]);
}
